// @ts-strict-ignore
import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import useForm from 'antd/lib/form/hooks/useForm';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import * as React from 'react';
import { getIsAmazonAppStore, getIsGooglePlayStore, isGooglePlatform } from '@/helpers';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType } from '@/types';
import { AppBuildComponent, CIBuildComponent } from '../BuildComponents';
import LinkageIcon from '../icons/LinkageIcon';
import Text from 'antd/lib/typography/Text';
import { displayConsoleNameForAppStoreType } from '@/helpers/displayHelpers';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import { forEach, reduce } from 'lodash';
import Tooltip from 'antd/lib/tooltip';
import { runwayColors } from '../styles/globalStyles';
import { InfoCircleOutlined, WarningFilled } from '@ant-design/icons';
import { formatDateWithTimezone } from '@/timeHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';

export interface BuildSelectionField {
    name: string;
    label: string;
    // Shows the required asterisk only; antd's `required` adds no validation rule
    required: boolean;
    multiple: boolean;
    showRecentBuilds: boolean;
    showPastBuildsEmptyState: boolean;
    showsClearButton?: boolean;
    currentSelection?: AppStoreAvailableBuild[];
    // Builds already picked in this other field are shown disabled here, so the same build can't be picked twice
    excludeBuildsSelectedInField?: string;
    excludeReason?: string;
    labelTooltip?: React.ReactNode;
}

interface ChangeSelectedBuildModalBaseProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    title: string;
    isLoading: boolean;
    includeAssociatedCIBuilds: boolean;
    open: boolean;
    fields: BuildSelectionField[];
    availableBuilds: AppStoreAvailableBuild[];
    onSaveChanges: (buildIdsByFieldName: Record<string, string[]>) => void;
    onClose: () => void;
    onSuccess: () => void;
}

const toBuildIds = (value: string | string[]): string[] => {
    if (!value) {
        return [];
    }

    return typeof value === 'string' ? [value] : value;
};

const ChangeSelectedBuildModalBase: React.FC<ChangeSelectedBuildModalBaseProps> = (
    props: ChangeSelectedBuildModalBaseProps,
) => {
    const [form] = useForm();
    const formValues = Form.useWatch([], form);
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const onSaveChanges = () => {
        form.validateFields().then((values) => {
            const buildIdsByFieldName = props.fields.reduce(
                (acc, field) => {
                    acc[field.name] = toBuildIds(values[field.name]);

                    return acc;
                },
                {} as Record<string, string[]>,
            );

            props.onSaveChanges(buildIdsByFieldName);
        });
    };

    const extraBuildInfo = (appStoreBuild: AppStoreAvailableBuild) => {
        if (appStoreBuild.isCurrentlySelected && appStoreBuild.isNewestAppStoreBuild) {
            return <Text>(latest, selected)</Text>;
        } else if (appStoreBuild.isCurrentlySelected) {
            return <Text>(currently selected)</Text>;
        } else if (appStoreBuild.isNewestAppStoreBuild) {
            return <Text>(latest)</Text>;
        }

        return null;
    };

    const renderAvailableBuild = (
        availableBuild: AppStoreAvailableBuild,
        includeAssociatedCIBuild: boolean,
        forceDisabled: boolean,
        excludeReason?: string,
    ) => {
        const isInternalOnly = !!availableBuild.appStoreBuild.isInternal;

        const baseBuildInfo = (
            <Space>
                {isInternalOnly && <WarningFilled style={{ color: runwayColors.statusYellow }} />}
                <AppBuildComponent
                    integrationId={availableBuild.appStoreBuild.integrationId}
                    buildIdentifier={availableBuild.appStoreBuild.buildNumber}
                    isProcessed={availableBuild.appStoreBuild.isProcessed}
                />
                {includeAssociatedCIBuild && (
                    <>
                        <LinkageIcon width={16} height={16} />
                        {availableBuild.associatedCIBuild && (
                            <CIBuildComponent
                                integrationId={availableBuild.associatedCIBuild.integrationId}
                                buildIdentifier={availableBuild.associatedCIBuild.buildIdentifier}
                            />
                        )}
                        {!availableBuild.associatedCIBuild && <Text type="secondary">Unknown</Text>}
                        {extraBuildInfo(availableBuild)}
                        {availableBuild.associatedCIBuild?.commitMessage && (
                            <Text type="secondary" ellipsis>
                                - {availableBuild.associatedCIBuild.commitMessage}
                            </Text>
                        )}
                    </>
                )}
            </Space>
        );

        const optionDisabled =
            forceDisabled || !!excludeReason || !availableBuild.appStoreBuild.isProcessed || isInternalOnly;

        const uploadTimestamp =
            getIsGooglePlayStore(props.appStoreType) && availableBuild.appStoreBuild.uploadedAt ?
                formatDateWithTimezone(
                    availableBuild.appStoreBuild.uploadedAt,
                    'short',
                    effectiveTimezone,
                    use24HourTimeFormat,
                )
            :   null;

        const rowTooltipTitle =
            excludeReason ? excludeReason
            : isInternalOnly ?
                `This build is marked as "Internal Only" in App Store Connect and can't be submitted for App Store review.`
            : uploadTimestamp ? `Uploaded ${uploadTimestamp}`
            : null;

        return (
            <Select.Option
                key={availableBuild.appStoreBuild.id}
                value={availableBuild.appStoreBuild.id}
                disabled={optionDisabled}
                label={baseBuildInfo}
            >
                <Space direction="vertical" size={0} style={{ opacity: optionDisabled ? 0.5 : 1.0 }}>
                    {rowTooltipTitle ?
                        <Tooltip title={rowTooltipTitle} placement="topLeft">
                            {baseBuildInfo}
                        </Tooltip>
                    :   baseBuildInfo}
                    {availableBuild.tracks && availableBuild.tracks.length > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            - Found on tracks: {availableBuild.tracks.join(', ')}
                        </Text>
                    )}
                </Space>
            </Select.Option>
        );
    };

    React.useEffect(() => {
        if (props.open) {
            forEach(props.fields, (field) => {
                form.setFieldsValue({ [field.name]: initialValueForField(field, props.availableBuilds) });
            });
        }
    }, [props.open]);

    const recentBuilds = [];
    const previousBuilds = [];

    forEach(props.availableBuilds, (build: AppStoreAvailableBuild) => {
        if (build.isRecentBuild) {
            recentBuilds.push(build);
        } else {
            previousBuilds.push(build);
        }
    });

    const isAmazonAppStore = getIsAmazonAppStore(props.appStoreType);

    // Past builds for Amazon are the builds fetched from the Amazon Appstore, and we can't actually interact with them
    const shouldDisablePastBuildSelection = isAmazonAppStore;

    return (
        <Modal
            title={props.title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: props.isLoading }}
            onCancel={props.onClose}
            destroyOnClose
            open={props.open}
            okText="Save changes"
            cancelText="Discard changes"
            afterClose={() => form.resetFields()}
            width={600}
        >
            <Form form={form} layout="vertical" onFinish={onSaveChanges}>
                {props.fields.map((field) => {
                    const excludedBuildIds =
                        field.excludeBuildsSelectedInField ?
                            toBuildIds(formValues?.[field.excludeBuildsSelectedInField])
                        :   [];
                    const excludeReasonForBuild = (build: AppStoreAvailableBuild) =>
                        excludedBuildIds.includes(build.appStoreBuild.id) ? field.excludeReason : null;

                    return (
                        <React.Fragment key={field.name}>
                            <FormItem
                                label={
                                    <Space>
                                        <Text>{field.label}</Text>
                                        {field.labelTooltip && (
                                            <Tooltip title={field.labelTooltip}>
                                                <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey }} />
                                            </Tooltip>
                                        )}
                                    </Space>
                                }
                                name={field.name}
                                required={field.required}
                            >
                                <Select
                                    allowClear={field.showsClearButton}
                                    placeholder="Select"
                                    mode={field.multiple ? 'multiple' : null}
                                    optionLabelProp="label"
                                >
                                    {field.showRecentBuilds && (
                                        <Select.OptGroup
                                            label={buildsLabelForPlatform(props.platform, true, props.appStoreType)}
                                        >
                                            {recentBuilds.map((build) =>
                                                renderAvailableBuild(
                                                    build,
                                                    props.includeAssociatedCIBuilds,
                                                    false,
                                                    excludeReasonForBuild(build),
                                                ),
                                            )}
                                            {recentBuilds.length === 0 && (
                                                <Select.Option disabled>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        No recent builds
                                                    </Text>
                                                </Select.Option>
                                            )}
                                        </Select.OptGroup>
                                    )}
                                    {(field.showPastBuildsEmptyState || previousBuilds.length !== 0) && (
                                        <Select.OptGroup
                                            label={buildsLabelForPlatform(props.platform, false, props.appStoreType)}
                                        >
                                            {previousBuilds.map((build) =>
                                                renderAvailableBuild(
                                                    build,
                                                    props.includeAssociatedCIBuilds,
                                                    shouldDisablePastBuildSelection,
                                                    excludeReasonForBuild(build),
                                                ),
                                            )}
                                            {field.showPastBuildsEmptyState && previousBuilds.length === 0 && (
                                                <Select.Option disabled>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        No recent builds
                                                    </Text>
                                                </Select.Option>
                                            )}
                                        </Select.OptGroup>
                                    )}
                                </Select>
                            </FormItem>
                        </React.Fragment>
                    );
                })}
            </Form>
        </Modal>
    );
};

export default ChangeSelectedBuildModalBase;

const initialValueForField = (field: BuildSelectionField, availableBuilds: AppStoreAvailableBuild[]) => {
    const emptyValue = field.multiple ? [] : null;

    if (!field.currentSelection || field.currentSelection.length === 0) {
        return emptyValue;
    }

    const atLeastOneSelectedBuildAvailableInList = reduce(
        field.currentSelection,
        (buildAvailableInList, build) => {
            if (buildAvailableInList) {
                return true;
            }

            return !!availableBuilds.find(
                (availableBuild) => availableBuild.appStoreBuild.id === build.appStoreBuild.id,
            );
        },
        false,
    );

    if (!atLeastOneSelectedBuildAvailableInList) {
        return emptyValue;
    }

    const buildIds = field.currentSelection.map((build) => build.appStoreBuild.id);

    return field.multiple ? buildIds : buildIds[0];
};

const buildsLabelForPlatform = (platform: AppPlatform, isRecent: boolean, appStoreType?: AppStoreType) => {
    let tooltipText = null;
    let labelText = isRecent ? 'Recent builds' : 'Past builds';

    if (isGooglePlatform(platform)) {
        const isAmazonAppStore = getIsAmazonAppStore(appStoreType);
        const storeText = displayConsoleNameForAppStoreType(appStoreType);

        if (isAmazonAppStore) {
            labelText = isRecent ? 'Recent builds' : `${storeText} builds`;
            tooltipText =
                isRecent ?
                    `Recent builds available for upload to the ${storeText}`
                :   `Version codes associated with the Upcoming Version edit in the ${storeText}`;
        } else {
            tooltipText =
                isRecent ?
                    `Version codes higher than that of the last release in ${storeText}`
                :   `Version codes equal to or less than that of the last release in ${storeText}`;
        }
    }

    return (
        <Space align="center">
            <Text>{labelText}</Text>
            {tooltipText && (
                <Tooltip title={tooltipText}>
                    <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey }} />
                </Tooltip>
            )}
        </Space>
    );
};
